import axios from 'axios';

// KYB API Base URL - use localhost:5000 in development, production URL otherwise
const KYB_BASE_URL = process.env.NODE_ENV === 'development' 
  // ? 'http://localhost:5000/api/v1/kyb'  // Commented out - uncomment when needed
  ? 'https://api.v1.indexx.ai/api/v1/kyb'  // Using production URL for now
  : 'https://api.v1.indexx.ai/api/v1/kyb';


// Create KYB API instance
const KYB_API = axios.create({
  baseURL: KYB_BASE_URL,
});

// Add request interceptor to include auth token
KYB_API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    
    // Validate token exists and is not null/undefined/empty
    if (token && token !== 'null' && token !== 'undefined' && token.trim() !== '') {
      if (config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } else {
      console.warn('⚠️ No valid access token found in localStorage');
      // Don't add Authorization header if token is invalid
    }
    
    // Don't override Content-Type for FormData - let axios handle it
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for better error handling
KYB_API.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.code === 'ECONNREFUSED') {
      error.message = 'Cannot connect to backend server. Please ensure it\'s running on localhost:5000';
    } else if (error.response?.status === 401) {
      const token = localStorage.getItem('access_token');
      console.error('❌ 401 Unauthorized - Token may be expired or invalid');
      console.error('Token exists:', !!token);
      console.error('Token value:', token ? `${token.substring(0, 20)}...` : 'null');
      
      // Clear invalid token
      if (token) {
        console.warn('⚠️ Clearing potentially invalid token');
        localStorage.removeItem('access_token');
      }
      
      error.message = 'Authentication required. Please log in again.';
      
      // Redirect to login or show login popup
      if (typeof window !== 'undefined') {
        // Check if we're on the KYB page and show login popup
        if (window.location.pathname.includes('kyb-verification')) {
          // The component should handle showing login popup
          console.warn('⚠️ User needs to log in again');
        }
      }
    } else if (error.response?.status === 404) {
      error.message = 'KYB endpoints not found. Please check backend configuration.';
    } else if (error.response?.status >= 500) {
      error.message = 'Backend server error. Please try again later.';
    }
    return Promise.reject(error);
  }
);

// Types
export type KybApplicationStatus =
  | 'draft'
  | 'pending'
  | 'under_review'
  | 'approved'
  | 'rejected'
  | 'need_more_info';

export interface KybStatus {
  hasApplication?: boolean;
  application?: {
    _id: string;
    status: KybApplicationStatus;
    businessInfo?: BusinessInfo;
    selectedEntityType?: string;
    selectedCountry?: string;
    levelRequested?: number;
    riskScore?: number;
    rejectionReason?: string | null;
    documentsCount?: number;
    directorsCount?: number;
    ubosCount?: number;
    hasTaxInfo?: boolean;
    allUBOsCompletedKYC?: boolean;
    createdAt?: string;
    updatedAt?: string;
  };
}

export interface BusinessInfo {
  legalName: string;
  tradingName?: string;
  registrationNumber: string;
  countryOfIncorporation: string;
  address: string;
  website?: string;
  businessType?: string;
  industryCode?: string;
  yearOfIncorporation?: string;
  numberOfEmployees?: string;
  businessDescription?: string;
}

export interface ApplicationDocument {
  _id: string;
  type: string;
  s3Key: string;
  mimeType: string;
  fileSize: number;
  side?: 'front' | 'back';
  createdAt: string;
  viewUrl?: string; // Presigned URL for viewing the document
}

export interface Director {
  _id?: string;
  kybApplicationId?: string;
  userId?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  dateOfBirth?: string; // YYYY-MM-DD
  nationality?: string;
  address?: string;
  countryOfResidence?: string;
  position: string;
  ownershipPercentage?: number;
  idDocumentType?: string;
  idDocumentFront?: string;
  idDocumentBack?: string;
  selfieImage?: string;
  pepStatus?: 'yes' | 'no';
  sanctionsStatus?: 'yes' | 'no';
  createdAt?: string;
  updatedAt?: string;
}

export interface DirectorPayload {
  businessOwnerEmail?: string | null;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  dateOfBirth?: string;
  nationality?: string;
  countryOfResidence?: string;
  address?: string;
  position: string;
  ownershipPercentage?: number;
  idDocumentType: string;
  idDocumentImage: string | null;
  idDocumentMimeType: string | null;
  idDocumentFrontImage: string | null;
  idDocumentFrontMimeType: string | null;
  idDocumentBackImage: string | null;
  idDocumentBackMimeType: string | null;
  selfieImage: string | null;
  selfieMimeType: string | null;
  isPep: boolean;
  isSanctioned: boolean;
}

export interface UBO {
  _id?: string;
  kybApplicationId?: string;
  userId?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  dateOfBirth?: string; // YYYY-MM-DD
  nationality?: string;
  address?: string;
  countryOfResidence?: string;
  ownershipPercentage: number;
  relationshipToCompany: string; // "Shareholder" | "Beneficial Owner" | "Trust Beneficiary"
  idDocumentType?: string;
  idDocumentFront?: string;
  idDocumentBack?: string;
  selfieImage?: string;
  kycStatus?: 'pending' | 'in_progress' | 'completed' | 'failed';
  kycApplicationId?: string | null;
  pepStatus?: 'yes' | 'no';
  sanctionsStatus?: 'yes' | 'no';
  createdAt?: string;
  updatedAt?: string;
}

export interface UBOPayload {
  businessOwnerEmail?: string | null;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  dateOfBirth?: string;
  nationality?: string;
  countryOfResidence?: string;
  address?: string;
  ownershipPercentage: number;
  relationshipToCompany: string;
  idDocumentType: string;
  idDocumentImage: string | null;
  idDocumentMimeType: string | null;
  idDocumentFrontImage: string | null;
  idDocumentFrontMimeType: string | null;
  idDocumentBackImage: string | null;
  idDocumentBackMimeType: string | null;
  selfieImage: string | null;
  selfieMimeType: string | null;
  isPep: boolean;
  isSanctioned: boolean;
}

export interface TaxInfo {
  _id?: string;
  kybApplicationId?: string;
  userId?: string;
  taxIdentificationNumber: string;
  taxCountry: string;
  vatNumber?: string;
  fatcaStatus?: 'applicable' | 'not_applicable' | 'unsure';
  taxDocumentS3Key?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ComplianceInfo {
  _id?: string;
  kybApplicationId?: string;
  userId?: string;
  purposeOfUse: string;
  expectedMonthlyVolume: string;
  expectedTransactionCount: string;
  expectedTransactionSize: string;
  sourceOfFunds: string;
  sourceOfWealth: string;
  dealsWithCrypto: boolean;
  dealsWithCashIntensive: boolean;
  dealsWithHighRiskCountries: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface KybApplication {
  _id: string;
  userId?: string;
  userEmail?: string;
  levelRequested?: number;
  selectedEntityType?: string;
  selectedCountry?: string;
  status?: KybApplicationStatus;
  businessInfo?: BusinessInfo;
  riskScore?: number;
  rejectionReason?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

// Document types for KYB
export type KybDocumentType = 
  | 'CERTIFICATE_OF_INCORPORATION'
  | 'SHAREHOLDER_REGISTER'
  | 'DIRECTOR_ID_FRONT'
  | 'DIRECTOR_ID_BACK'
  | 'BUSINESS_PROOF_OF_ADDRESS'
  | 'TAX_IDENTIFICATION'
  | 'COMPANY_BANK_STATEMENT'
  | 'AUTHORIZED_SIGNATORY_SELFIE';

/**
 * Create KYB Application
 */
export const createApplication = async (payload?: {
  levelRequested?: number;
  entityType?: string;
  country?: string;
}): Promise<{ status: number; data: KybApplication }> => {
  try {
    const response = await KYB_API.post('/applications', payload || {});
    return response;
  } catch (error: any) {
    console.error('Failed to create KYB application:', error);
    throw error.response?.data || error;
  }
};

/**
 * Update Business Information
 */
export const updateBusinessInfo = async (
  applicationId: string,
  businessInfo: BusinessInfo
): Promise<{ status: number; data: KybApplication }> => {
  try {
    const response = await KYB_API.put(`/applications/${applicationId}/business-info`, {
      businessInfo,
    });
    return response;
  } catch (error: any) {
    console.error('Failed to update business info:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get current KYB status
 */
export const getKybStatus = async (): Promise<{ status: number; data: KybStatus }> => {
  try {
    const response = await KYB_API.get('/status');
    return response;
  } catch (error: any) {
    // Don't log 404 as error - it's normal when no application exists
    if (error.response?.status === 404) {
      console.log('No KYB application found (404) - this is normal for new users');
    } else {
      console.error('Failed to get KYB status:', error);
    }
    throw error.response?.data || error;
  }
};

/**
 * Compress and resize image to reduce file size
 */
const compressImage = (file: File, maxWidth: number = 1920, maxHeight: number = 1920, quality: number = 0.8): Promise<File> => {
  return new Promise((resolve, reject) => {
    // If it's a PDF, return as-is
    if (file.type === 'application/pdf') {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to compress image'));
              return;
            }
            const compressedFile = new File([blob], file.name, {
              type: file.type || 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          },
          file.type || 'image/jpeg',
          quality
        );
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

/**
 * Upload document directly to backend
 */
export const uploadDocumentDirect = async (
  applicationId: string,
  type: KybDocumentType,
  file: File
): Promise<{ 
  status: number;
  data: ApplicationDocument;
}> => {
  try {
    // Validate file size before processing (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      throw new Error('File size must be less than 10MB');
    }

    // Compress image if it's an image file (not PDF)
    let processedFile = file;
    if (file.type && file.type.startsWith('image/') && file.type !== 'image/gif') {
      try {
        processedFile = await compressImage(file, 1920, 1920, 0.8);
        console.log(`Image compressed: ${file.size} bytes -> ${processedFile.size} bytes`);
      } catch (compressError) {
        console.warn('Failed to compress image, using original:', compressError);
        // Continue with original file if compression fails
      }
    }

    // Check compressed size (limit to 5MB for base64 payload)
    const maxPayloadSize = 5 * 1024 * 1024; // 5MB (base64 will be ~33% larger)
    if (processedFile.size > maxPayloadSize) {
      throw new Error('File is too large. Please use a smaller file or compress it further.');
    }

    // Convert File to base64 string (data URL format)
    const base64Image = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Result is in data URL format: "data:image/jpeg;base64,..."
        resolve(result);
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(processedFile);
    });

    // Log payload size for debugging
    const payloadSize = new Blob([base64Image]).size;
    console.log('Uploading KYB document:', {
      applicationId,
      type,
      fileName: file.name,
      originalSize: file.size,
      processedSize: processedFile.size,
      payloadSize,
      fileType: file.type,
    });

    // Backend expects JSON body with base64 image string
    const requestBody = {
      applicationId,
      type,
      image: base64Image,
      mimeType: processedFile.type || 'image/jpeg',
    };

    const response = await KYB_API.post('/documents/upload', requestBody, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    return response;
  } catch (error: any) {
    console.error('Failed to upload KYB document:', error);
    
    // Handle specific error cases
    if (error.message && error.message.includes('too large')) {
      throw { 
        response: { 
          data: { 
            message: 'File is too large. Please use a smaller file (max 5MB recommended).' 
          } 
        } 
      };
    }
    
    if (error.response?.status === 413 || error.response?.status === 500) {
      const errorData = error.response?.data;
      if (errorData?.message?.includes('too large') || errorData?.message?.includes('PayloadTooLarge')) {
        throw {
          response: {
            data: {
              message: 'File is too large for upload. Please compress the image or use a smaller file (max 2-3MB recommended).'
            }
          }
        };
      }
    }
    
    throw error.response?.data || error;
  }
};

/**
 * Get application documents
 */
export const getApplicationDocuments = async (
  applicationId: string
): Promise<{ 
  status: number;
  data: {
    documents: ApplicationDocument[];
  }
}> => {
  try {
    const response = await KYB_API.get(`/applications/${applicationId}/documents`);
    return response;
  } catch (error: any) {
    console.error('Failed to get KYB application documents:', error);
    throw error.response?.data || error;
  }
};

/**
 * Delete a KYB application document
 */
export const deleteKybDocument = async (
  applicationId: string,
  documentId: string
): Promise<{ status: number; data: { message: string } }> => {
  try {
    const response = await KYB_API.delete(`/applications/${applicationId}/documents/${documentId}`);
    return response;
  } catch (error: any) {
    console.warn('Primary KYB delete document endpoint failed, attempting fallback', error);
    if (error.response?.status === 404) {
      try {
        const fallback = await KYB_API.delete(`/documents/${documentId}`);
        return fallback;
      } catch (fallbackError: any) {
        console.error('Fallback delete for KYB document also failed:', fallbackError);
        throw fallbackError.response?.data || fallbackError;
      }
    }
    console.error('Failed to delete KYB document:', error);
    throw error.response?.data || error;
  }
};

/**
 * Add Director
 */
export const addDirector = async (
  applicationId: string,
  director: DirectorPayload
): Promise<{ status: number; data: Director }> => {
  try {
    const response = await KYB_API.post(`/applications/${applicationId}/directors`, director);
    return response;
  } catch (error: any) {
    console.error('Failed to add director:', error);
    throw error.response?.data || error;
  }
};

/**
 * Delete Director
 */
export const deleteDirector = async (directorId: string): Promise<{ status: number; data: { message: string } }> => {
  try {
    const response = await KYB_API.delete(`/directors/${directorId}`);
    return response;
  } catch (error: any) {
    console.error('Failed to delete director:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get Directors
 */
export const getDirectors = async (applicationId: string): Promise<{ status: number; data: { directors: Director[] } }> => {
  try {
    const response = await KYB_API.get(`/applications/${applicationId}/directors`);
    return response;
  } catch (error: any) {
    console.error('Failed to get directors:', error);
    throw error.response?.data || error;
  }
};

/**
 * Add UBO
 */
export const addUBO = async (
  applicationId: string,
  ubo: UBOPayload
): Promise<{ status: number; data: UBO }> => {
  try {
    const response = await KYB_API.post(`/applications/${applicationId}/ubos`, ubo);
    return response;
  } catch (error: any) {
    console.error('Failed to add UBO:', error);
    throw error.response?.data || error;
  }
};

/**
 * Delete UBO
 */
export const deleteUBO = async (uboId: string): Promise<{ status: number; data: { message: string } }> => {
  try {
    const response = await KYB_API.delete(`/ubos/${uboId}`);
    return response;
  } catch (error: any) {
    console.error('Failed to delete UBO:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get UBOs
 */
export const getUBOs = async (applicationId: string): Promise<{ status: number; data: { ubos: UBO[] } }> => {
  try {
    const response = await KYB_API.get(`/applications/${applicationId}/ubos`);
    return response;
  } catch (error: any) {
    console.error('Failed to get UBOs:', error);
    throw error.response?.data || error;
  }
};

/**
 * Update UBO KYC Status
 */
export const updateUBOKycStatus = async (
  uboId: string,
  kycStatus: 'pending' | 'in_progress' | 'completed' | 'failed',
  kycApplicationId?: string
): Promise<{ status: number; data: UBO }> => {
  try {
    const response = await KYB_API.put(`/ubos/${uboId}/kyc-status`, {
      kycStatus,
      kycApplicationId,
    });
    return response;
  } catch (error: any) {
    console.error('Failed to update UBO KYC status:', error);
    throw error.response?.data || error;
  }
};

/**
 * Add Tax Information
 */
export const addTaxInfo = async (
  applicationId: string,
  taxInfo: Omit<TaxInfo, '_id' | 'kybApplicationId' | 'userId' | 'createdAt' | 'updatedAt'>
): Promise<{ status: number; data: TaxInfo }> => {
  try {
    const response = await KYB_API.post(`/applications/${applicationId}/tax-info`, taxInfo);
    return response;
  } catch (error: any) {
    console.error('Failed to add tax info:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get Tax Information
 */
export const getTaxInfo = async (applicationId: string): Promise<{ status: number; data: TaxInfo | null }> => {
  try {
    const response = await KYB_API.get(`/applications/${applicationId}/tax-info`);
    return response;
  } catch (error: any) {
    console.error('Failed to get tax info:', error);
    throw error.response?.data || error;
  }
};

/**
 * Update Compliance Information
 */
export const updateComplianceInfo = async (
  applicationId: string,
  complianceInfo: Omit<ComplianceInfo, '_id' | 'kybApplicationId' | 'userId' | 'createdAt' | 'updatedAt'>
): Promise<{ status: number; data: ComplianceInfo }> => {
  try {
    const response = await KYB_API.put(`/applications/${applicationId}/compliance-info`, complianceInfo);
    return response;
  } catch (error: any) {
    console.error('Failed to update compliance info:', error);
    throw error.response?.data || error;
  }
};

/**
 * Get Compliance Information
 */
export const getComplianceInfo = async (
  applicationId: string
): Promise<{ status: number; data: ComplianceInfo | null }> => {
  try {
    const response = await KYB_API.get(`/applications/${applicationId}/compliance-info`);
    return response;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return { status: 404, data: null };
    }
    console.error('Failed to get compliance info:', error);
    throw error.response?.data || error;
  }
};

/**
 * Submit KYB application for review
 */
export const submitKybApplication = async (
  applicationId: string
): Promise<{ status: number; data: KybApplication }> => {
  try {
    const response = await KYB_API.post(`/applications/${applicationId}/submit`);
    return response;
  } catch (error: any) {
    console.error('Failed to submit KYB application:', error);
    throw error.response?.data || error;
  }
};
